import { BigNumber } from 'bignumber.js';
import { VialMetrics, CalculatorInputs } from '../../types/calculator';
import { DosageUnit } from '../../types/data';

// Configure BigNumber
BigNumber.config({
  DECIMAL_PLACES: 4,
  ROUNDING_MODE: BigNumber.ROUND_DOWN
});

function standardizeToMg(value: number, unit: DosageUnit): BigNumber {
  const valueBN = new BigNumber(value);
  
  switch (unit) {
    case 'mg':
      return valueBN;
    case 'mcg':
      return valueBN.dividedBy(1000);
    case 'g':
      return valueBN.multipliedBy(1000);
    case 'ml':
      return valueBN;
    default:
      throw new Error(`Unknown unit: ${unit}`);
  }
}

export function calculateVialMetrics(inputs: CalculatorInputs): VialMetrics {
  const {
    vialAmount,
    vialUnits,
    dosage,
    dosageUnits,
    targetSyringeUnits
  } = inputs;

  // Standardize inputs to mg
  const amountInMg = standardizeToMg(vialAmount, vialUnits);
  const dosageInMg = standardizeToMg(dosage, dosageUnits);
  const targetUnitsBN = new BigNumber(targetSyringeUnits);

  // Calculate optimal reconstitution amount
  // amount_in_mg / ((dosage_in_mg / target_units_per_dose) * 100)
  const reconstitutionAmount = amountInMg.dividedBy(
    dosageInMg.dividedBy(targetUnitsBN).multipliedBy(100)
  );

  // Calculate final concentration (mg/ml)
  // (dosage_in_mg / target_units_per_dose) * 100
  const finalConcentration = dosageInMg.dividedBy(targetUnitsBN).multipliedBy(100);

  // Calculate dose in milliliters
  // dosage_in_mg / ((dosage_in_mg / target_units_per_dose) * 100)
  const doseMilliliters = dosageInMg.dividedBy(
    dosageInMg.dividedBy(targetUnitsBN).multipliedBy(100)
  );

  // Calculate doses available
  // FLOOR(amount_in_mg / dosage_in_mg)
  const totalDoses = amountInMg.dividedBy(dosageInMg).integerValue(BigNumber.ROUND_FLOOR);

  return {
    reconstitutionAmount: reconstitutionAmount.toNumber(),
    reconstitutionUnits: 'ml',
    finalConcentration: finalConcentration.toNumber(),
    unitsPerDose: targetSyringeUnits,
    doseMilliliters: doseMilliliters.toNumber(),
    totalDoses: totalDoses.toNumber()
  };
}
